import { Injectable, inject } from '@angular/core';
import {
  AysClinicByConditionResult,
  AysClinicMetadata,
  AysClinicSearchResult,
  AysClinicSnIdentifier,
  AysClinicSnSearchItem,
  AysClinicSnSearchPage,
  AysClinicSnSummary,
  BdAddress,
  ClinicReadService,
} from '@bindoc-projects/clinic-domain-lib';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { AysClinicSnService } from "./clinic-sn.service";
import { AYS_CLINIC_ADDRESS_WITH_LOCATION } from "./transformations";

/**
 * DE × SN-native implementation of the country-neutral {@link ClinicReadService}
 * contract, by COMPOSITION over the SN-native `AysClinicSnService`.
 *
 * Since BDA-5809 step 3 the contract speaks the opaque `clinicSn` itself, so this
 * adapter is close to pass-through: it no longer resolves an `(ik, sn)` pair into
 * a clinicSn, and no longer down-maps SN responses onto the legacy
 * `AysClinic`/`AysClinicSearchResponseItem` shapes.
 *
 * - `get`    — `metadata()`. The former second call to `overview()` existed only
 *   to fill `AysClinic.beds`, which the lean summary does not carry and no
 *   contract consumer could read, so `get()` is now one request instead of two.
 * - `search` — paginated `/clinics/search`, first page, reported as an
 *   {@link AysClinicSnSearchPage} so callers can see the match count is larger
 *   than the page they were handed.
 * - `getAll` — `byClinicSns()` (`POST /clinics/by-condition` with a `clinicSn`
 *   condition), one batched call for all refs. The condition resolves BOTH iNEK
 *   and synthetic `L-{ik}-{sn}` clinicSns — including a correct name and address
 *   — since backend `0104ef247` deployed (verified on bda-5580, BDA-5749). The
 *   endpoint returns one row per (clinic × QB year); we dedupe to the latest year
 *   per clinic.
 *
 * Bound by meta-app's `CLINIC_PROVIDER` (BDA-5742 step 8 DE flip).
 */
@Injectable({
  providedIn: 'root',
})
export class ClinicSnReadAdapter implements ClinicReadService {
  private readonly _sn = inject(AysClinicSnService);

  get(ref: AysClinicSnIdentifier): Observable<AysClinicSnSummary> {
    return this._sn
      .metadata(ref.clinicSn, ref.year)
      .pipe(map((metadata) => this._toSummary(ref, metadata)));
  }

  search(q: string): Observable<AysClinicSnSearchPage> {
    return this._sn.search(q).pipe(
      map((page) => {
        const items = (page?.content ?? []).map((result) =>
          this._toSearchItem(result),
        );

        // The real server-side count: this backend paginates, so `items` is one
        // page and may well be a fraction of the matches. Falling back to
        // `items.length` only covers a response without the metadata, where
        // "there is more" is not something we can honestly claim.
        return { items, total: page?.totalElements ?? items.length };
      }),
    );
  }

  getAll(refs: AysClinicSnIdentifier[]): Observable<AysClinicSnSearchItem[]> {
    if (refs.length === 0) {
      return of([]);
    }

    return this._sn.byClinicSns(refs.map((ref) => ref.clinicSn)).pipe(
      map((results) =>
        this._latestPerClinic(results).map((result) =>
          this._toSearchItemFromByCondition(result),
        ),
      ),
    );
  }

  private _toSummary(
    ref: AysClinicSnIdentifier,
    metadata: AysClinicMetadata | null,
  ): AysClinicSnSummary {
    return {
      // Carry the caller's ref through — round-trips trivially.
      id: ref,
      name: metadata?.name ?? null,
      address: this._addressWithLocation(metadata),
    };
  }

  /**
   * SN metadata carries the clinic's coordinates on the top-level `location`
   * ({lat,lng}), NOT inside `address`. Consumers read them as `address.location`
   * in `[lng, lat]` order — e.g. scenario-analysis feeds `address.location[1]`
   * (lat) / `[0]` (lng) into the by-distance search. Surface the coords there;
   * without this, `get()` returns an address with no `location` and those
   * consumers crash.
   */
  private _addressWithLocation(
    metadata: AysClinicMetadata | null,
  ): BdAddress | null {
    return AYS_CLINIC_ADDRESS_WITH_LOCATION(
      metadata?.address,
      metadata?.location,
    );
  }

  /**
   * `/clinics/search` splits address and coordinates exactly as `metadata` does,
   * so the same fold applies — without it a picked search result has no
   * `address.location` and every consumer reading it off a selection breaks
   * (the cube map tile's "Adresse von Klinik" centre threw, leaving the tile on
   * its previous centre). Kept through the BDA-5809 step-3 re-type: only the
   * RETURN shape became SN-typed, the fold still has to happen here.
   */
  private _toSearchItem(result: AysClinicSearchResult): AysClinicSnSearchItem {
    return {
      // `ik` is display-only on the identifier; identity is the clinicSn.
      id: new AysClinicSnIdentifier(result.clinicSn, undefined, result.clinicIk),
      name: result.name ?? null,
      address:
        AYS_CLINIC_ADDRESS_WITH_LOCATION(result.address, result.location) ??
        new BdAddress(),
    };
  }

  /**
   * by-condition returns one row per (clinic × QB year); collapse to a single
   * row per clinicSn (latest year) so `getAll` yields one item per clinic.
   */
  private _latestPerClinic(
    results: AysClinicByConditionResult[],
  ): AysClinicByConditionResult[] {
    const byClinic = new Map<string, AysClinicByConditionResult>();
    for (const result of results) {
      const existing = byClinic.get(result.clinicSn);
      if (!existing || (result.year ?? 0) > (existing.year ?? 0)) {
        byClinic.set(result.clinicSn, result);
      }
    }
    return [...byClinic.values()];
  }

  private _toSearchItemFromByCondition(
    result: AysClinicByConditionResult,
  ): AysClinicSnSearchItem {
    return {
      // Deliberately NO year: the pre-step-3 item id carried none either, and
      // pinning the deduped row's year here would make these ids compare unequal
      // to the year-less refs consumers hold.
      id: new AysClinicSnIdentifier(result.clinicSn, undefined, result.clinicIk),
      name: result.name ?? null,
      address: result.address,
    };
  }
}
